import { Link } from "react-router-dom";
import { Wrench, Cog, ShieldCheck, ArrowRight } from "lucide-react";
import WhatsAppIcon from "./WhatsAppIcon";

const WA_NUMBER = "524424319100";
const waLink = (msg) =>
  `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;

const services = [
  {
    icon: Cog,
    num: "01",
    key: "fabricacion",
    title: "Fabricación a Medida",
    desc: "Diseño y manufactura en acero inoxidable de guardas telescópicas y fuelles bajo plano técnico o muestra física. Geometría original OEM.",
    cta: {
      label: "Cotizar Fabricación",
      href: waLink("Hola, me interesa cotizar la fabricación de una guarda telescópica o fuelle con plano o muestra"),
      style: "wa",
    },
  },
  {
    icon: Wrench,
    num: "02",
    key: "reparacion",
    title: "Reparación Urgente",
    badge: "24/7",
    desc: "Reconstrucción estructural, desabollado de cajas telescópicas, alineación milimétrica y diagnóstico urgente para paros de producción en CNC.",
    cta: {
      label: "Ver Servicio de Reparación",
      to: "/reparacion-de-guardas-telescopicas-cnc",
      style: "cyan",
    },
  },
  {
    icon: ShieldCheck,
    num: "03",
    key: "mantenimiento",
    title: "Mantenimiento Preventivo y Refacciones",
    desc: "Sustitución de wipers (rascadores CL1), guías de desgaste en Nylamid, patines de latón y lamelas para extender la vida útil de tus equipos.",
    cta: {
      label: "Cotizar Mantenimiento",
      href: waLink("Hola, me interesa cotizar el mantenimiento preventivo y refacciones (wipers, guías, lamelas) para mi maquinaria CNC"),
      style: "wa",
    },
  },
];

function ServiceCta({ cta, testid }) {
  const classes =
    cta.style === "wa"
      ? "bg-[#25D366] hover:bg-[#1eb85a] text-white"
      : "bg-[#00B4D8] hover:bg-[#0096B4] text-white";
  const inner = (
    <>
      {cta.style === "wa" ? (
        <WhatsAppIcon size={16} className="shrink-0" />
      ) : (
        <ArrowRight size={16} className="shrink-0" />
      )}
      <span>{cta.label}</span>
    </>
  );
  const base = `inline-flex items-center justify-center gap-2 px-5 py-3 text-xs font-semibold uppercase tracking-[0.1em] transition-all duration-300 hover:-translate-y-0.5 ${classes}`;
  if (cta.to) {
    return (
      <Link to={cta.to} data-testid={testid} className={base} style={{ borderRadius: "2px" }}>
        {inner}
      </Link>
    );
  }
  return (
    <a
      href={cta.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className={base}
      style={{ borderRadius: "2px" }}
    >
      {inner}
    </a>
  );
}

export default function Services() {
  return (
    <section
      id="servicios"
      data-testid="services-section"
      className="gf-section bg-[#061226] relative"
    >
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#00B4D8]/50 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-[#00B4D8]" />
              <span className="gf-overline">Nuestros Pilares</span>
            </div>
            <h2 className="font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
              Tres servicios. Una sola promesa:
              <span className="text-[#00B4D8]"> precisión sostenida.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-white/60 text-base leading-relaxed">
              Atendemos cada necesidad de tu línea de producción con
              soluciones técnicas integrales que protegen tu inversión y
              garantizan continuidad operativa.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.key}
                data-testid={`service-card-${s.key}`}
                className="group bg-[#0A1B35] border border-white/10 hover:border-[#00B4D8] p-8 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(0,180,216,0.25)]"
                style={{ borderRadius: "2px" }}
              >
                <div className="flex items-start justify-between mb-8">
                  <div
                    className="w-14 h-14 bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-[#00B4D8] transition-colors"
                    style={{ borderRadius: "2px" }}
                  >
                    <Icon size={24} className="text-[#00B4D8]" strokeWidth={2} />
                  </div>
                  <div className="flex items-center gap-2">
                    {s.badge && (
                      <span
                        className="bg-[#25D366]/15 text-[#25D366] font-mono-accent text-[10px] uppercase tracking-widest px-2 py-1 border border-[#25D366]/30"
                        style={{ borderRadius: "2px" }}
                      >
                        {s.badge}
                      </span>
                    )}
                    <span className="font-mono-accent text-xs text-white/40 tracking-widest">
                      {s.num} / 03
                    </span>
                  </div>
                </div>

                <h3 className="font-display font-black text-2xl text-white mb-3 tracking-tight leading-tight">
                  {s.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed mb-8 flex-1">
                  {s.desc}
                </p>

                <ServiceCta cta={s.cta} testid={`service-cta-${s.key}`} />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
